/**
 * @voltxampere/engine — Inductor transient companion stamp
 *
 * SPECS §3.8 inductor companion. Per the Sprint 6 design (planner
 * keeps the inductor's `["vsource"]` augmentation in transient too)
 * the stamp lays the standard ±1 KVL couplings AND a `-Geq` term on
 * the augmented row's diagonal:
 *
 *   row k: V_pos − V_neg − Geq · I_L(t) = Ieq
 *
 * which is the linearised form of `V_L(t) = Geq·I_L(t) + Ieq`. The
 * inductor branch current is the augmented row's unknown, so the
 * previous step's I_L is read straight from `xPrev[k]` (no per-
 * component current history needed — unlike the TRAP capacitor).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { selectInductorCompanion } from "../../transient/integration/dispatcher.js";
import { defineTransientStamp } from "./types.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
function nodeVoltage(x, idx) {
    if (idx < 0)
        return 0;
    return x[idx] ?? 0;
}
export const inductorTransientStamp = (ctx, component) => {
    const L = getNumericParam(component, "inductance");
    const posIdx = ctx.nodeIndex(getTerminalNetId(component, "p1"));
    const negIdx = ctx.nodeIndex(getTerminalNetId(component, "p2"));
    const k = ctx.vSourceIndex(component.id);
    // I_L and V_L at the previous *step* — frozen across this step's
    // Newton iterations; `ctx.xPrev` is the current iterate and would
    // give wrong companion values inside Newton.
    const ILPrev = ctx.xStepPrev[k] ?? 0;
    const VLPrev = nodeVoltage(ctx.xStepPrev, posIdx) - nodeVoltage(ctx.xStepPrev, negIdx);
    const ILPrevPrev = ctx.xStepPrevPrev === null ? null : (ctx.xStepPrevPrev[k] ?? 0);
    const { Geq, Ieq } = selectInductorCompanion({
        method: ctx.method,
        L,
        h: ctx.h,
        hPrev: ctx.hPrev,
        ILPrev,
        VLPrev,
        ILPrevPrev,
    });
    // Standard voltage-source-style ±1 couplings between the augmented
    // row/col and the two terminals.
    ctx.addY(posIdx, k, 1);
    ctx.addY(negIdx, k, -1);
    ctx.addY(k, posIdx, 1);
    ctx.addY(k, negIdx, -1);
    // The inductive resistance lands on the augmented diagonal with a
    // negative sign — see the file-level docstring's row equation.
    ctx.addY(k, k, -Geq);
    // RHS for the augmented row.
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + Ieq;
};
/** Inductor keeps the `["vsource"]` augmentation in transient (SPECS §3.8). */
export const inductorTransientStampDefinition = defineTransientStamp("inductor", inductorTransientStamp, ["vsource"]);
//# sourceMappingURL=inductor.js.map